import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import NowLiveBanner from "@/components/NowLiveBanner";
import DiscordServerCard from "@/components/DiscordServerCard";
import TopShowsWidget from "@/components/TopShowsWidget";
import api from "@/lib/api";

export default function Home() {
  const [stats, setStats] = useState({ depts: null, brands: null, shows: null, hosts: null });
  const [deptList, setDeptList] = useState([]);
  const [brandsByDept, setBrandsByDept] = useState({});

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [d, b, s, h] = await Promise.all([
          api.get("/departments"), api.get("/brands"), api.get("/shows"), api.get("/hosts"),
        ]);
        if (!alive) return;
        setStats({ depts: d.data.length, brands: b.data.length, shows: s.data.length, hosts: h.data.length });
        setDeptList(d.data);
        const grouped = {};
        b.data.forEach((x) => { (grouped[x.department_id] = grouped[x.department_id] || []).push(x); });
        setBrandsByDept(grouped);
      } catch (e) { /* keep silent */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const fmt = (n) => (n == null ? "—" : n < 10 ? String(n).padStart(2, "0") : String(n));

  return (
    <div data-testid="home-page">
      <NowLiveBanner />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pb-24 w-full">
          <div className="label-eyebrow mb-6 text-[var(--ff-red)]">▮ EST. 2026 / MULTI-BRAND PRODUCTION NETWORK</div>
          <h1 className="font-display text-[clamp(3rem,9vw,9rem)] mb-8 max-w-6xl">
            BUILT FOR <span className="text-[var(--ff-red)]">SOUND</span>,<br />
            STAGE & <span className="italic font-light">SCREEN</span>.
          </h1>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl">
            <p className="md:col-span-2 text-lg text-[var(--ff-text-dim)] leading-relaxed">
              Freshy Fresh Productions LLC operates a portfolio of brands across music, film, broadcast, and digital media — producing original content that lives on screens, stages, and streams worldwide.
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/schedule" className="btn-primary justify-center" data-testid="hero-schedule-btn">
                <Play size={14} /> Live Schedule
              </Link>
              <Link to="/departments" className="btn-ghost text-center" data-testid="hero-departments-btn">
                Explore Brands <ArrowRight size={14} className="inline ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-[var(--ff-border)]" data-testid="home-stats-strip">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4">
          <div className="p-8 lg:p-12 border-r border-[var(--ff-border)]">
            <div className="font-display text-5xl lg:text-6xl mb-2" data-testid="stat-depts">{fmt(stats.depts)}</div>
            <div className="label-eyebrow">DEPARTMENTS</div>
          </div>
          <div className="p-8 lg:p-12 border-r border-[var(--ff-border)]">
            <div className="font-display text-5xl lg:text-6xl mb-2" data-testid="stat-brands">{fmt(stats.brands)}</div>
            <div className="label-eyebrow">BRANDS</div>
          </div>
          <div className="p-8 lg:p-12 border-r border-[var(--ff-border)]">
            <div className="font-display text-5xl lg:text-6xl mb-2" data-testid="stat-shows">{fmt(stats.shows)}</div>
            <div className="label-eyebrow">SHOWS ON AIR</div>
          </div>
          <div className="p-8 lg:p-12">
            <div className="font-display text-5xl lg:text-6xl mb-2" data-testid="stat-hosts">{fmt(stats.hosts)}</div>
            <div className="label-eyebrow">HOSTS</div>
          </div>
        </div>
      </section>

      <TopShowsWidget />

      {/* About preview */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="label-eyebrow mb-4">▮ THE STORY</div>
          <h2 className="font-display text-5xl lg:text-7xl mb-6">A NETWORK,<br/>NOT A LABEL.</h2>
        </div>
        <div className="lg:col-span-7 space-y-6 text-[var(--ff-text-dim)] text-base leading-relaxed">
          <p className="text-white text-lg">
            Freshy Fresh started as a single studio — and grew into a full production network operating across four departments and seven dedicated brands.
          </p>
          <p>
            From late-night shows to feature films, from concert promotion to podcast networks, every brand under the Freshy Fresh umbrella exists to bring sound, stage and screen audiences something they can't find anywhere else.
          </p>
          <Link to="/about" className="inline-flex items-center gap-3 font-mono-ui text-xs uppercase tracking-[0.2em] border-b border-[var(--ff-red)] pb-1 text-[var(--ff-red)]" data-testid="about-read-more">
            Read the full story <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Brand grid preview */}
      <section className="border-t border-[var(--ff-border)] bg-[var(--ff-surface)]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-32">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <div className="label-eyebrow mb-4">▮ WHAT WE BUILD</div>
              <h2 className="font-display text-5xl lg:text-7xl">
                {stats.depts != null ? `${fmt(stats.depts)}` : "•"}<br/>DEPARTMENT{stats.depts === 1 ? "" : "S"}.
              </h2>
            </div>
            <Link to="/departments" className="btn-ghost" data-testid="departments-cta">View All Brands →</Link>
          </div>
          {deptList.length === 0 ? (
            <div className="surface-card p-12 text-center text-[var(--ff-text-dim)] font-mono-ui text-xs uppercase tracking-[0.2em]">
              No departments yet — add them from the admin dashboard.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ff-border)]" data-testid="home-dept-grid">
              {deptList.slice(0, 8).map((d, i) => {
                const dBrands = brandsByDept[d.id] || [];
                return (
                  <Link key={d.id} to="/departments" className="bg-[var(--ff-bg)] p-8 lg:p-10 hover:bg-[var(--ff-surface-hover)] transition-colors group flex flex-col" data-testid={`dept-preview-${i}`}>
                    <div className="font-mono-ui text-xs text-[var(--ff-red)] mb-12">{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-display text-2xl mb-3 leading-tight uppercase">{d.name}</div>
                    <div className="text-sm text-[var(--ff-text-dim)] flex-1">
                      {dBrands.length > 0
                        ? dBrands.slice(0, 3).map((b) => b.name).join(" · ") + (dBrands.length > 3 ? ` +${dBrands.length - 3}` : "")
                        : <span className="italic">No brands yet</span>}
                    </div>
                    <div className="mt-8 flex items-center justify-between">
                      <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{dBrands.length} BRAND{dBrands.length === 1 ? "" : "S"}</div>
                      <ArrowRight className="text-[var(--ff-red)] opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <DiscordServerCard />

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-32 grid md:grid-cols-2 gap-12">
        <div className="surface-card p-12">
          <div className="label-eyebrow mb-4 text-[var(--ff-red)]">▮ CAREERS</div>
          <h3 className="font-display text-4xl mb-6">JOIN THE NETWORK.</h3>
          <p className="text-[var(--ff-text-dim)] mb-8">Production, post, marketing, broadcast. Open roles across every department.</p>
          <Link to="/careers" className="btn-primary" data-testid="careers-cta">Apply Now →</Link>
        </div>
        <div className="surface-card p-12">
          <div className="label-eyebrow mb-4">▮ SCHEDULE</div>
          <h3 className="font-display text-4xl mb-6">WHAT'S ON.</h3>
          <p className="text-[var(--ff-text-dim)] mb-8">Sunday through Saturday. 2:00 AM – 12:00 AM. Live on YouTube.</p>
          <Link to="/schedule" className="btn-ghost" data-testid="schedule-cta">View Schedule →</Link>
        </div>
      </section>
    </div>
  );
}
